import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { SEO } from "@/components/SEO";
import { Reveal } from "@/components/Reveal";
import { GeoPanel } from "@/components/GeoPanel";
import api from "@/lib/api";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const load = useCallback(() => {
    api.get(`/blog/${slug}`).then((r) => setPost(r.data)).catch(() => setNotFound(true));
  }, [slug]);
  useEffect(() => { load(); }, [load]);

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center" data-testid="blog-notfound">
        <h1 className="font-serif text-4xl text-espresso">Article not found.</h1>
        <Link to="/resources" className="inline-block mt-6 text-champagne hover:text-espresso transition-colors">← Back to Resources</Link>
      </div>
    );
  }

  if (!post) return <div className="py-32 text-center font-serif text-2xl text-espresso animate-pulse">Loading…</div>;

  return (
    <div data-testid="blog-post-page">
      <SEO title={post.title} description={post.excerpt} />
      <article className="max-w-3xl mx-auto px-6 lg:px-10 pt-14 md:pt-20 pb-24">
        <Reveal>
          <Link to="/resources" className="inline-flex items-center gap-2 text-espresso/60 hover:text-espresso transition-colors text-sm" data-testid="blog-back">
            <ArrowLeft size={16} /> Back to Resources
          </Link>
          <span className="block text-xs uppercase tracking-[0.25em] text-champagne font-medium mt-8">
            {post.category === "business" ? "Business Insights" : "Career Insights"}
          </span>
          <h1 className="font-serif text-4xl md:text-5xl text-espresso mt-4 leading-tight">{post.title}</h1>
          <p className="text-espresso/50 text-sm mt-4">{post.author} · {new Date(post.created_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}</p>
        </Reveal>
        <Reveal delay={0.1}>
          <GeoPanel variant={post.category === "business" ? "forest" : "champagne"} label="people, redefined." className="rounded-3xl my-10 aspect-[16/9]" />
        </Reveal>
        <Reveal delay={0.15}>
          <div className="prose prose-lg max-w-none">
            {post.content.split("\n\n").map((para, i) => (
              <p key={`${post.slug}-${i}`} className="text-espresso/80 text-lg leading-relaxed mb-6">{para}</p>
            ))}
          </div>
        </Reveal>
        <Reveal>
          <div className="mt-14 bg-taupe rounded-2xl p-8 text-center">
            <h3 className="font-serif text-2xl text-espresso">Ready to put this into action?</h3>
            <Link to="/book" className="inline-block mt-5 bg-espresso text-ivory rounded-full px-7 py-3 font-medium hover:bg-forest transition-colors" data-testid="blog-cta-book">Book a Consultation</Link>
          </div>
        </Reveal>
      </article>
    </div>
  );
}
